import type { IconName } from '../components/Icon.js';

import type { ApplicationStatus } from '../fixtures/domain.js';
import { canAssignApplicationStatus, closingVariantFor, HIRING_STAGES } from '../fixtures/rules.js';
import { ENDING_META, STAGE_META } from './stageMeta.js';

/**
 * Every move the card can offer: the pipeline, plus the ending. The header's own
 * vocabulary minus Details, which changes nothing about the application and so
 * is not a move at all.
 */
export const CARD_ACTIONS = [...HIRING_STAGES, 'end'] as const;

export type CardAction = (typeof CARD_ACTIONS)[number];

/**
 * The number an action answers to, fixed rather than per conversation.
 *
 * A direct chat shows a lone End row keyed 4, with 1 to 3 belonging to stages it
 * has no application to apply them to. That gap is the point rather than a cost:
 * a key that moves between conversations cannot be learned, End is ⇧4
 * everywhere, and the drafts below always start at ⇧5.
 */
export function shortcutFor(action: CardAction): number {
  return CARD_ACTIONS.indexOf(action) + 1;
}

export type ActionTone = 'accent' | 'danger';

export interface ActionLook {
  icon: IconName;
  label: string;
  tone: ActionTone;
  /**
   * This stage is the one currently set.
   *
   * The glyph fills and the row stops being an offer: pressing it would assign a
   * stage the application already has. It reads as a state, which is the whole of
   * what a filled glyph means here — a stage is drawn filled once it is set, and
   * not before.
   */
  done: boolean;
}

/**
 * How an action is drawn and named.
 *
 * The wording and the glyph are lifted from `stageMeta` rather than invented
 * here. The card offers a button that also exists a few inches up, so
 * the two have to be recognisably the same button: a heart in one place and a
 * bookmark in the other reads as two different acts, and an employer who takes a
 * suggestion should see the header land exactly where the card said it would.
 *
 * `tone` follows the header's colouring too. All three stages share the accent,
 * because the header does not distinguish them either. Ending is danger in both
 * its forms: the wrap-up on a direct chat rejects nobody, but it still closes the
 * conversation and starts the deletion clock, and "not a rejection" is not the
 * same as "not destructive".
 */
export function actionLook(
  action: CardAction,
  jobBounded: boolean,
  status: ApplicationStatus,
): ActionLook {
  if (action === 'end') {
    // One action the conversation reads two ways — a rejection on an
    // application, a wrap-up on a direct chat. Which of the two is
    // `closingVariantFor`'s answer rather than a branch of its own: the card and
    // the header have to agree, and they agree by asking the same question. The
    // colour does not split: both end a chat.
    return { ...ENDING_META[closingVariantFor(jobBounded)], tone: 'danger', done: false };
  }

  const meta = STAGE_META[action];
  // One name whether or not the stage is set: `done` fills the glyph, and that
  // is the whole of how this list says which row is a fact rather than an offer.
  return { icon: meta.icon, label: meta.name, tone: 'accent', done: status === action };
}

/**
 * The action rows the fallback list draws, in card order.
 *
 * Includes the stage the application already has: that row is drawn as an inert
 * state rather than an offer, and it is still on screen. Shared with the key
 * layer so the two cannot disagree about what an employer can see.
 */
export function visibleActions(jobBounded: boolean, status: ApplicationStatus): CardAction[] {
  return CARD_ACTIONS.filter((action) => {
    if (action === 'end') return true;
    return jobBounded && (status === action || canAssignApplicationStatus(status, action));
  });
}

/**
 * Which action ⇧digit would take, given what the card is currently drawing — or
 * null when that key names nothing on screen.
 *
 * The keys are numbered against the fixed list, but only this function knows
 * which rows the open conversation actually offers. Without it the key layer
 * answered from the numbering alone: ⇧4 opened End & reject on a direct chat's
 * card, and ⇧1 re-assigned a stage whose row was drawn inert.
 *
 * It no longer narrows to the assistant's own mark. It used to — a recommended
 * decision drew one row, so one key answered — and the card now draws every move
 * the conversation allows with the mark on one of them. A key layer still
 * answering for the marked row alone would leave three visible, numbered,
 * pressable rows whose keys did nothing, which is worse than a key that names a
 * row nobody can see: the employer can see these.
 */
export function actionForShortcut(
  digit: number,
  jobBounded: boolean,
  status: ApplicationStatus,
): CardAction | null {
  const action = CARD_ACTIONS[digit - 1];
  if (!action) return null;
  // Already reached: drawn filled and inert, and it drops its shortcut chip.
  if (status === action) return null;

  return visibleActions(jobBounded, status).includes(action) ? action : null;
}
